import React from 'react';
import { DEFAULT_HEADING_SIZE_MAP, HeadingSizeMap, TextColorOption, useComponentDefaultProps } from '../../../theming';
import { StylerBaseProps, Styler } from '../../Styler';
import { useStyles } from './TypographyStyler.styles';

export interface TypographyStylerProps extends StylerBaseProps {
    /**
     * The HTML content to apply styling to.
     */
    children: React.ReactNode;

    /**
     * The color of heading tags
     * @default text.heading
     */
    headingColor?: TextColorOption;

    /**
     * The color of anchor tags
     * @default text.link
     */
    anchorColor?: TextColorOption;

    /**
     * An object which maps html heading tags to honeycomb heading sizes.
     */
    headingSizeMap?: HeadingSizeMap;
}

const defaultProps: Partial<TypographyStylerProps> = {
    headingColor: 'text.heading',
    anchorColor: 'text.link',
    headingSizeMap: DEFAULT_HEADING_SIZE_MAP,
};

/**
 * The following component helps clean up any html tags which may be present within the content passed to it and reflect the
 * honeycomb version of that tag as per the theme. It can taken in content as children and will be able to display the content which
 * is HTML with consistency with the theme used across the app. It helps further customise the heading and anchor colours as well as heading mapper
 * to dictate the look and feel of these tags when they are transformed to in house tags for consistent behaviour.
 */
export function TypographyStyler(props: Readonly<TypographyStylerProps>): React.JSX.Element {
    const {
        headingColor = 'text.heading',
        anchorColor = 'text.link',
        headingSizeMap = DEFAULT_HEADING_SIZE_MAP,
        ref,
        ...other
    } = useComponentDefaultProps('TypographyStyler', defaultProps, props);
    const { styles } = useStyles({ headingColor, anchorColor, headingSizeMap });

    return <Styler ref={ref} baseStyles={styles.root} data-typography-styler={true} {...other} />;
}
